import { gql } from '@apollo/client'
import * as Apollo from '@apollo/client'

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

export interface AccountDeletionResponse {
  success: boolean
  message: string
}

export interface DeleteAccountMutation {
  deleteAccount: AccountDeletionResponse
}

export interface DeleteAccountMutationVariables {
  password: string
}

export interface DeleteSingleListingMutation {
  deleteSingleListing: AccountDeletionResponse
}

export interface DeleteSingleListingMutationVariables {
  listingId: number
}

export interface DeleteAllListingsMutation {
  deleteAllListings: AccountDeletionResponse
}

export interface DeactivateListingMutation {
  deactivateListing: AccountDeletionResponse
}

export interface DeactivateListingMutationVariables {
  listingId: number
}

export interface ReactivateListingMutation {
  reactivateListing: AccountDeletionResponse
}

export interface ReactivateListingMutationVariables {
  listingId: number
}

// ──────────────────────────────────────────────────────────────────────────────
// Documents
// ──────────────────────────────────────────────────────────────────────────────

export const DELETE_ACCOUNT = gql`
  mutation DeleteAccount($password: String!) {
    deleteAccount(password: $password) {
      success
      message
    }
  }
`

export const DELETE_SINGLE_LISTING = gql`
  mutation DeleteSingleListing($listingId: Int!) {
    deleteSingleListing(listingId: $listingId) {
      success
      message
    }
  }
`

export const DELETE_ALL_LISTINGS = gql`
  mutation DeleteAllListings {
    deleteAllListings {
      success
      message
    }
  }
`

export const DEACTIVATE_LISTING = gql`
  mutation DeactivateListing($listingId: Int!) {
    deactivateListing(listingId: $listingId) {
      success
      message
    }
  }
`

export const REACTIVATE_LISTING = gql`
  mutation ReactivateListing($listingId: Int!) {
    reactivateListing(listingId: $listingId) {
      success
      message
    }
  }
`

// ──────────────────────────────────────────────────────────────────────────────
// Hooks
// ──────────────────────────────────────────────────────────────────────────────

export function useDeleteAccountMutation(
  baseOptions?: Apollo.MutationHookOptions<DeleteAccountMutation, DeleteAccountMutationVariables>
) {
  return Apollo.useMutation<DeleteAccountMutation, DeleteAccountMutationVariables>(
    DELETE_ACCOUNT,
    baseOptions
  )
}

export function useDeleteSingleListingMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeleteSingleListingMutation,
    DeleteSingleListingMutationVariables
  >
) {
  return Apollo.useMutation<
    DeleteSingleListingMutation,
    DeleteSingleListingMutationVariables
  >(DELETE_SINGLE_LISTING, baseOptions)
}

export function useDeleteAllListingsMutation(
  baseOptions?: Apollo.MutationHookOptions<DeleteAllListingsMutation, Record<string, never>>
) {
  return Apollo.useMutation<DeleteAllListingsMutation, Record<string, never>>(
    DELETE_ALL_LISTINGS,
    baseOptions
  )
}

export function useDeactivateListingMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeactivateListingMutation,
    DeactivateListingMutationVariables
  >
) {
  return Apollo.useMutation<
    DeactivateListingMutation,
    DeactivateListingMutationVariables
  >(DEACTIVATE_LISTING, baseOptions)
}

export function useReactivateListingMutation(
  baseOptions?: Apollo.MutationHookOptions<
    ReactivateListingMutation,
    ReactivateListingMutationVariables
  >
) {
  return Apollo.useMutation<
    ReactivateListingMutation,
    ReactivateListingMutationVariables
  >(REACTIVATE_LISTING, baseOptions)
}
